// src/components/JarvislyComponents/DocumentInfoIcons.jsx
// *******************************************************************************
// DOCUMENT INFO ICONS
// *******************************************************************************
// Displays icons indicating document characteristics like origin (manual,
// migrated, automatic, AI-generated) with tooltips for additional details.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Tooltip } from 'antd';
import {
  ApiOutlined,
  DesktopOutlined,
  FileSyncOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import dayjs from 'dayjs';
import useZustand from 'hooks/useZustand';
import aiGenerativeIcon from 'assets/images/ai-generative.png';
import aiIcon from 'assets/images/ai.png';
import { MdOutlineKeyboardAlt } from 'react-icons/md';
import { PiPlugsConnected } from 'react-icons/pi';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DOCUMENT_ORIGIN = {
  MANUAL: 0,
  MIGRATED: 1,
  AUTOMATIC: 2,
  AI: 3,
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DocumentInfoIcons = ({ data, size = 16 }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const currentTheme = useZustand('theme', 'currentTheme');
  const isDark = currentTheme === 'dark';

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const icons = [];

  // Origin icon
  const originIcon = buildOriginIcon(data, size, isDark);
  if (originIcon) {
    icons.push(originIcon);
  }

  // Enrichment icon (API or AI)
  const enrichmentIcon = buildEnrichmentIcon(data, size, isDark);
  if (enrichmentIcon) {
    icons.push(enrichmentIcon);
  }

  // API Integration icon
  const apiIntegrationIcon = buildApiIntegrationIcon(data, size);
  if (apiIntegrationIcon) {
    icons.push(apiIntegrationIcon);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (icons.length === 0) return null;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      {icons.map((icon, index) => (
        <span
          key={index}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          {icon}
        </span>
      ))}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DocumentInfoIcons;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Build origin icon based on document metadata origin value
 * @param {Object} data - documents)
 * @param {number} size - Icon size in pixels
 * @param {boolean} isDark - Whether the theme is dark
 * @returns {JSX.Element|null} Icon with tooltip or null
 */
export function buildOriginIcon(data, size = 16, isDark = false) {
  const origin = data?._metadata?.origin;
  const migrationInfo = data?.migrationInfo;

  const iconStyle = { fontSize: size, color: '#8c8c8c' };

  switch (origin) {
    case DOCUMENT_ORIGIN.MIGRATED: {
      const migratedAt = migrationInfo?.migratedAt
        ? dayjs(migrationInfo.migratedAt).format('DD/MM/YYYY [às] HH:mm:ss')
        : '—';
      const source = migrationInfo?.origin || '—';
      const tooltip = translateX('document_origin_migrated', { migratedAt, source });

      return (
        <Tooltip title={tooltip}>
          <ImportOutlined style={iconStyle} />
        </Tooltip>
      );
    }

    case DOCUMENT_ORIGIN.AUTOMATIC:
      return (
        <Tooltip title={translateX('document_origin_automatic')}>
          <DesktopOutlined style={{ ...iconStyle }} />
        </Tooltip>
      );

    case DOCUMENT_ORIGIN.AI:
      return (
        <Tooltip title={translateX('document_origin_ai')}>
          <img
            src={aiGenerativeIcon}
            alt="AI"
            width={size}
            height={size}
            style={{
              opacity: 0.5,
              filter: isDark ? 'invert(1)' : 'none',
            }}
          />
        </Tooltip>
      );

    case DOCUMENT_ORIGIN.MANUAL:
    default:
      return (
        <Tooltip title={translateX('document_origin_manual')}>
          <MdOutlineKeyboardAlt style={iconStyle} />
        </Tooltip>
      );
  }
}

/**
 * Build enrichment icon based on dataEnrichment object
 * Shows AI icon if enriched by AI, FileSyncOutlined if enriched by API
 * @param {Object} data - Data document
 * @param {number} size - Icon size in pixels
 * @param {boolean} isDark - Whether the theme is dark
 * @returns {JSX.Element|null} Icon with tooltip or null
 */
export function buildEnrichmentIcon(data, size = 16, isDark = false) {
  const dataEnrichment = data?.dataEnrichment;

  const isEnrichedByApi = dataEnrichment?.cnpj?.isEnriched || dataEnrichment?.cpf?.isEnriched;

  if (!isEnrichedByApi) return null;

  const iconStyle = { fontSize: size, color: '#8c8c8c' };
  const isEnrichedByAI = dataEnrichment?.ai?.isEnriched === true;

  if (isEnrichedByAI) {
    const provider = dataEnrichment.ai?.provider || 'AI';
    const model = dataEnrichment.ai?.model || '';
    const enrichedAt = dataEnrichment.ai?.enrichedAt
      ? dayjs(dataEnrichment.ai.enrichedAt).format('DD/MM/YYYY [às] HH:mm:ss')
      : null;

    const tooltip = translateX('document_enriched_by_ai', { provider, model, enrichedAt });

    return (
      <Tooltip title={tooltip}>
        <img
          src={aiIcon}
          alt="AI"
          width={size}
          height={size}
          style={{
            opacity: 0.5,
            filter: isDark ? 'invert(1)' : 'none',
          }}
        />
      </Tooltip>
    );
  }

  // Enriched by API
  return (
    <Tooltip title={translateX('document_enriched_by_api')}>
      <FileSyncOutlined style={iconStyle} />
    </Tooltip>
  );
}

/**
 * Build API integration icon
 * Shows if operator has API integration configured (enabled or disabled)
 */
export function buildApiIntegrationIcon(data, size = 16) {
  const apiCode = data?._integrations?.operatorCode;
  const apiEnabled = data?._integrations?.apiEnabled;

  if (!apiCode) return null; // No API configured

  const iconStyle = { fontSize: size };

  if (apiEnabled) {
    // API enabled
    return (
      <Tooltip title={translateX('api_enabled')}>
        <PiPlugsConnected style={{ ...iconStyle, color: '#8c8c8c' }} />
      </Tooltip>
    );
  } else {
    // API configured but disabled
    return (
      <Tooltip title={translateX('api_disabled')}>
        <ApiOutlined style={{ ...iconStyle, color: '#d9d9d9' }} />
      </Tooltip>
    );
  }
}
